import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {useFastlyQuery} from "@khan/data-gql";
import {gqlOp} from "@khan/data-gql-core";
import {RequestInfo} from "@khan/shared-core";
import {cssUrl, toAbsoluteUrl, useLocation} from "@khan/urls";

import ErrorLoadingPage from "#app-shell-package/errors/error-loading-page.tsx";
import NotFound from "#app-shell-package/errors/not-found.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import {JsonLd, learningVideoSchema} from "#components/json-ld/json-ld.tsx";
import {usePageSettings} from "#data-access/use-page-settings.ts";
import {getLocaleStatus} from "#shared-package/locale-status.ts";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";
import VideoPlayerModel from "#video-package/video-player-model.ts";
import YoutubePlayer from "#video-package/video-player/youtube-player.tsx";

import {withStrings} from "./locales/lite-video/load-strings.tsx";

import "../../stylesheets/homepage-lite-package/homepage-lite.less";

import type {GqlOperation} from "@khan/data-gql-core";
import {LocaleStatus} from "@khan/i18n";

import type {
    getVideoListForLiteLocale,
    getVideoListForLiteLocaleVariables,
    getVideoListForLiteLocale_videoListingForLocale as VideoListingForLocale,
    getVideoListForLiteLocale_videoListingForLocale_videos as VideoLocale,
} from "./__graphql-types__/getVideoListForLiteLocale.ts";
import type {
    LiteVideo_SlugFromYoutubeId as SlugFromYoutubeId,
    LiteVideo_SlugFromYoutubeIdVariables as SlugFromYoutubeIdVariables,
} from "./__graphql-types__/LiteVideo_SlugFromYoutubeId.ts";
import autoLink from "./autolink.ts";

export const LITE_VIDEO_QUERY: GqlOperation<
    getVideoListForLiteLocale,
    getVideoListForLiteLocaleVariables
> = gqlOp(gql`
    query getVideoListForLiteLocale($kaLocale: String!) {
        videoListingForLocale(kaLocale: $kaLocale) {
            videos {
                id
                youtubeId
                title
                description
            }
        }
    }
`);

export const GET_SLUG_FROM_YOUTUBE_ID_QUERY: GqlOperation<
    SlugFromYoutubeId,
    SlugFromYoutubeIdVariables
> = gqlOp(gql`
    query LiteVideo_SlugFromYoutubeId($youtubeId: String!) {
        videoByYoutubeId(youtubeId: $youtubeId) {
            id
            defaultUrlPath
        }
    }
`);

// This handles the legacy case of when users pass a youtubeId to the /video
// route, which would normally load the Lite Video page, but when it receives
// a youtubeId, it redirects to the regular video page based on the youtubeId
function RedirectFromYoutubeId(props: {youtubeId: string}): React.ReactElement {
    const result = useFastlyQuery(GET_SLUG_FROM_YOUTUBE_ID_QUERY, {
        variables: {youtubeId: props.youtubeId},
    });

    if (result.status === "loading" || result.status === "no-data") {
        return <RouteLoadSpinner />;
    }

    if (result.status === "error" || result.status === "aborted") {
        return <ErrorLoadingPage />;
    }

    const redirectPath = result.data.videoByYoutubeId?.defaultUrlPath ?? "";
    if (!redirectPath) {
        return <SmartRedirect to="/" />;
    }

    return <SmartRedirect to={redirectPath} />;
}

function LiteVideo(props: Record<any, any>): React.ReactElement {
    const location = useLocation();
    const {update: updatePageSettings} = usePageSettings();

    updatePageSettings({
        footer: false,
        header: false,
        title: t`Khan Academy`,
    });

    const result = useFastlyQuery(LITE_VIDEO_QUERY, {
        variables: {kaLocale: RequestInfo.kaLocale},
    });

    if (result.status === "loading" || result.status === "no-data") {
        return <RouteLoadSpinner />;
    }

    if (result.status === "error" || result.status === "aborted") {
        return <ErrorLoadingPage />;
    }

    // See if we can find the matching video listing that holds this particular
    // youtube video.
    const videoListingForLocale = result.data.videoListingForLocale || [];
    let matchingVideoListing: Nullable<VideoListingForLocale> = null;
    let prevVideo: Nullable<VideoLocale> = null;
    let video: Nullable<VideoLocale> = null;
    let nextVideo: Nullable<VideoLocale> = null;

    for (const videoListing of videoListingForLocale) {
        const videos = videoListing?.videos || [];
        for (const video of videos) {
            if (video?.youtubeId === location.queryParams.v) {
                matchingVideoListing = videoListing;
                break;
            }
        }

        if (matchingVideoListing) {
            break;
        }
    }

    if (matchingVideoListing) {
        const videos = matchingVideoListing?.videos || [];

        for (let i = 0; i < videos.length; i += 1) {
            if (videos[i]?.youtubeId === location.queryParams.v) {
                video = videos[i];
                if (i > 0) {
                    prevVideo = videos[i - 1];
                }
                if (i < videos.length - 1) {
                    nextVideo = videos[i + 1];
                }
                break;
            }
        }
    }

    // If we can't find one then we render a 404 page.
    if (!matchingVideoListing || !video) {
        return <NotFound />;
    }

    updatePageSettings({
        title: video?.title || t`Khan Academy`,
        description: video?.description || "",
        videoUrl: `http://www.youtube-nocookie.com/v/${video?.youtubeId || ""}`,
        videoEmbedUrl: `http://www.youtube-nocookie.com/embed/${video?.youtubeId || ""}`,
    });

    const {title, description, youtubeId} = video;

    const videoModel = new VideoPlayerModel({
        youtubeId,
        translatedYoutubeId: youtubeId,
        translatedYoutubeLang: RequestInfo.kaLocale,
        liteVideo: true,
    });

    return (
        <PageRoot>
            <View style={styles.videoPage}>
                <View style={styles.video}>
                    <JsonLd
                        schema={learningVideoSchema({
                            name: title,
                            description: description,
                            thumbnailUrl: `http://img.youtube.com/vi/${
                                youtubeId || ""
                            }/hqdefault.jpg`,
                            url: toAbsoluteUrl(`/video?v=${youtubeId || ""}`),
                        })}
                    />
                    <div data-role="header">
                        <div data-role="content">
                            <div>
                                <div id="description">
                                    <h1>
                                        <span>{title}</span>
                                        <br />
                                    </h1>
                                </div>
                                <View style={[styles.videoHeader]}>
                                    <nav>
                                        {prevVideo && (
                                            <label id="prev_video">
                                                <Link
                                                    rel="prev"
                                                    style={styles.prevVideo}
                                                    href={`/video?v=${
                                                        prevVideo.youtubeId ||
                                                        ""
                                                    }`}
                                                >
                                                    {prevVideo.title || ""}
                                                </Link>
                                            </label>
                                        )}
                                        {nextVideo && (
                                            <label id="next_video">
                                                <Link
                                                    rel="next"
                                                    style={styles.nextVideo}
                                                    href={`/video?v=${
                                                        nextVideo.youtubeId ||
                                                        ""
                                                    }`}
                                                >
                                                    {nextVideo.title || ""}
                                                </Link>
                                            </label>
                                        )}
                                        <div className="clear" />
                                    </nav>
                                </View>
                            </div>
                            <div>
                                <div>
                                    <div>
                                        <YoutubePlayer
                                            id="lite-video-player"
                                            model={videoModel}
                                            playbackRate="RATE_100_PERCENT"
                                            muteVideos={false}
                                            showCaptions={false}
                                            tabAccessible={false}
                                            title={video?.title || ""}
                                            startTime={0}
                                            onError={() => {}}
                                            onPlayerReady={() => {}}
                                            onStateChange={() => {}}
                                            updateMuteVideosSetting={() => {}}
                                            updateShowCaptionsSetting={() => {}}
                                            updatePlaybackRateSetting={() => {}}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: autoLink(description || ""),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </View>
            </View>
        </PageRoot>
    );
}

function LiteVideoWrapper(props: Record<any, any>): React.ReactElement {
    const location = useLocation();

    const localeStatus = getLocaleStatus(RequestInfo.kaLocale);

    if (localeStatus === LocaleStatus.LITE) {
        return <LiteVideo />;
    }

    // If ?v=youtubeId is passed in, redirect based on the youtube id
    if (location.queryParams.v) {
        return <RedirectFromYoutubeId youtubeId={location.queryParams.v} />;
    }

    return <SmartRedirect to="/" />;
}

export default withStrings(LiteVideoWrapper);

const styles = StyleSheet.create({
    videoPage: {
        padding: "20px 0 55px 0",
    },

    video: {
        width: 800,
        marginLeft: "auto",
        marginRight: "auto",
    },

    videoHeader: {
        marginTop: 22,
        minHeight: 1,
    },

    prevVideo: {
        display: "block",
        float: "left",
        marginRight: "1em",
        textAlign: "left",
        background: `${cssUrl(
            "/images/list-item-black-left.png",
        )} no-repeat 0% 6px`,
        paddingLeft: 10,
    },

    nextVideo: {
        display: "block",
        float: "right",
        marginLeft: "1em",
        textAlign: "right",
        background: `${cssUrl(
            "/images/list-item-black-right.png",
        )} no-repeat 100% 6px`,
        paddingRight: 10,
    },
});
